import React from 'react';
import { Empleado, BalanceEmpleado, PeriodoVacaciones, ResumenDepartamento } from '../tipos/vacaciones';
import './ResumenEmpresa.css';

interface PropiedadesResumenEmpresa {
  empleados: Empleado[];
  balances: BalanceEmpleado[];
  periodos: PeriodoVacaciones[];
  resumenesDepartamentos: ResumenDepartamento[];
}

const ResumenEmpresa: React.FC<PropiedadesResumenEmpresa> = ({
  empleados,
  balances,
  periodos,
  resumenesDepartamentos
}) => {
  const hoy = new Date();

  // Calcular estadísticas generales
  const totalEmpleados = empleados.filter(e => e.estaActivo).length;
  const empleadosEnLicencia = empleados.filter(emp => {
    return periodos.some(periodo => {
      const inicio = new Date(periodo.fechaInicio);
      const fin = new Date(periodo.fechaFin);
      return periodo.idEmpleado === emp.id && inicio <= hoy && fin >= hoy;
    });
  }).length;


  // Empleados actualmente con licencia
  const actualmenteEnLicencia = empleados.filter(emp => {
    return periodos.some(periodo => {
      const inicio = new Date(periodo.fechaInicio);
      const fin = new Date(periodo.fechaFin);
      return periodo.idEmpleado === emp.id && inicio <= hoy && fin >= hoy;
    });
  });

  // Próximas licencias (próximas 2 semanas)
  const dosSemanasDesdeAhora = new Date(hoy.getTime() + 14 * 24 * 60 * 60 * 1000);
  const proximasLicencias = periodos
    .filter(periodo => {
      const inicio = new Date(periodo.fechaInicio);
      return inicio > hoy && inicio <= dosSemanasDesdeAhora;
    })
    .sort((a, b) => new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime())
    .slice(0, 5);

  // Licencias por tipo este mes
  const mesActual = hoy.getMonth();
  const anioActual = hoy.getFullYear();
  const licenciasMensualesPorTipo = periodos
    .filter(periodo => {
      const inicio = new Date(periodo.fechaInicio);
      return inicio.getMonth() === mesActual && inicio.getFullYear() === anioActual;
    })
    .reduce((acumulador, periodo) => {
      switch (periodo.tipo) {
        case 'Vacaciones':
          acumulador.vacaciones += periodo.dias;
          break;
        case 'Licencia médica':
          acumulador.medicas += periodo.dias;
          break;
        case 'Día de Beneficio':
          acumulador.especiales += periodo.dias;
          break;
        case 'Compensatorio':
          acumulador.compensatorios += periodo.dias;
          break;
      }
      return acumulador;
    }, { vacaciones: 0, medicas: 0, especiales: 0, compensatorios: 0 });

  const obtenerLicenciaActual = (idEmpleado: number): PeriodoVacaciones | null => {
    return periodos.find(periodo => {
      const inicio = new Date(periodo.fechaInicio);
      const fin = new Date(periodo.fechaFin);
      return periodo.idEmpleado === idEmpleado && inicio <= hoy && fin >= hoy;
    }) || null;
  };

  const obtenerIconoTipo = (tipo: string) => {
    switch (tipo) {
      case 'Vacaciones':
        return 'fas fa-umbrella-beach';
      case 'Licencia médica':
        return 'fas fa-heartbeat';
      case 'Día de Beneficio':
        return 'fas fa-star';
      case 'Compensatorio':
        return 'fas fa-clock';
      default:
        return 'fas fa-calendar';
    }
  };

  const obtenerColorTipo = (tipo: string) => {
    switch (tipo) {
      case 'Vacaciones':
        return 'vacaciones';
      case 'Licencia médica':
        return 'medica';
      case 'Día de Beneficio':
        return 'especial';
      case 'Compensatorio':
        return 'compensatorio';
      default:
        return 'vacaciones';
    }
  };

  const formatearFecha = (cadenaFecha: string) => {
    return new Date(cadenaFecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const formatearRangoFechas = (fechaInicio: string, fechaFin: string) => {
    if (fechaInicio === fechaFin) {
      return formatearFecha(fechaInicio);
    }
    return `${formatearFecha(fechaInicio)} - ${formatearFecha(fechaFin)}`;
  };

  const nombresMeses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  return (
    <div className="company-summary">
      {/* Estadísticas principales */}
      <div className="summary-cards">
        <div className="summary-card employees">
          <div className="card-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="card-content">
            <h3>Total Empleados</h3>
            <span className="number">{totalEmpleados}</span>
          </div>
        </div>

        <div className="summary-card on-leave">
          <div className="card-icon">
            <i className="fas fa-user-clock"></i>
          </div>
          <div className="card-content">
            <h3>Con Licencia Hoy</h3>
            <span className="number">{empleadosEnLicencia}</span>
          </div>
        </div>
      </div>

      <div className="content-grid">
        {/* Empleados actualmente con licencia */}
        <div className="current-leave-section">
          <h2>
            <i className="fas fa-user-clock"></i>
            Con Licencia Actualmente
          </h2>
          {actualmenteEnLicencia.length > 0 ? (
            <div className="current-leave-list">
              {actualmenteEnLicencia.map(empleado => {
                const licencia = obtenerLicenciaActual(empleado.id);
                if (!licencia) return null;

                return (
                  <div key={empleado.id} className={`leave-item ${obtenerColorTipo(licencia.tipo)}`}>
                    <div className="leave-icon">
                      <i className={obtenerIconoTipo(licencia.tipo)}></i>
                    </div>
                    <div className="leave-info">
                      <div className="employee-name">{empleado.nombre}</div>
                      <div className="employee-dept">{empleado.departamento}</div>
                      <div className="leave-details">
                        <span className="leave-type">{licencia.tipo}</span>
                        <span className="leave-dates">{formatearRangoFechas(licencia.fechaInicio, licencia.fechaFin)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-leave">
              <i className="fas fa-check-circle"></i>
              <span>Todos los empleados están disponibles</span>
            </div>
          )}
        </div>

        {/* Próximas licencias */}
        <div className="upcoming-section">
          <h2>
            <i className="fas fa-calendar-week"></i>
            Próximas Licencias (2 semanas)
          </h2>
          {proximasLicencias.length > 0 ? (
            <div className="upcoming-list">
              {proximasLicencias.map(periodo => (
                <div key={periodo.id} className={`upcoming-item ${obtenerColorTipo(periodo.tipo)}`}>
                  <div className="upcoming-date">
                    {formatearRangoFechas(periodo.fechaInicio, periodo.fechaFin)}
                  </div>
                  <div className="upcoming-info">
                    <span className="upcoming-name">{periodo.nombreEmpleado}</span>
                    <span className="upcoming-type">{periodo.tipo}</span>
                  </div>
                  <div className="upcoming-days">
                    <span className="days-badge">{periodo.dias}d</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-upcoming">
              <i className="fas fa-calendar-check"></i>
              <span>No hay licencias programadas</span>
            </div>
          )}
        </div>

        {/* Resumen por departamentos */}
        <div className="departments-section">
          <h2>
            <i className="fas fa-building"></i>
            Resumen por Departamentos
          </h2>
          <div className="departments-grid">
            {resumenesDepartamentos.map(dept => (
              <div key={dept.departamento} className="department-card">
                <div className="dept-header">
                  <h3>{dept.departamento}</h3>
                  <span className="employee-count">{dept.totalEmpleados} empleados</span>
                </div>
                <div className="dept-stats">
                  <div className="stat">
                    <span className="stat-label">Con licencia</span>
                    <span className="stat-value">{dept.empleadosEnLicencia}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Días usados</span>
                    <span className="stat-value">{dept.totalDiasUsados}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Estadísticas del mes */}
        <div className="monthly-stats-section">
          <h2>
            <i className="fas fa-chart-bar"></i>
            Licencias en {nombresMeses[mesActual]} {anioActual}
          </h2>
          <div className="monthly-stats-grid">
            <div className="monthly-stat vacaciones">
              <div className="stat-icon">
                <i className="fas fa-umbrella-beach"></i>
              </div>
              <div className="stat-info">
                <span className="stat-number">{licenciasMensualesPorTipo.vacaciones}</span>
                <span className="stat-label">Días Vacaciones</span>
              </div>
            </div>
            <div className="monthly-stat medica">
              <div className="stat-icon">
                <i className="fas fa-heartbeat"></i>
              </div>
              <div className="stat-info">
                <span className="stat-number">{licenciasMensualesPorTipo.medicas}</span>
                <span className="stat-label">Días Médicas</span>
              </div>
            </div>
            <div className="monthly-stat especial">
              <div className="stat-icon">
                <i className="fas fa-star"></i>
              </div>
              <div className="stat-info">
                <span className="stat-number">{licenciasMensualesPorTipo.especiales}</span>
                <span className="stat-label">Días Especiales</span>
              </div>
            </div>
            <div className="monthly-stat compensatorio">
              <div className="stat-icon">
                <i className="fas fa-clock"></i>
              </div>
              <div className="stat-info">
                <span className="stat-number">{licenciasMensualesPorTipo.compensatorios}</span>
                <span className="stat-label">Compensatorios</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumenEmpresa;